import React, { useEffect, useState } from 'react'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { 
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  Font,
  Heading,
  List,
  Alignment,
  Link,
  PasteFromOffice,
  ImageInsertViaUrl,
  Image,
  ImageInsert,  Table, TableToolbar,
} from 'ckeditor5'
import { Box, Button, Divider, Paper, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { useSelector } from 'react-redux'
import { getAllCountryList } from '../../../../services/location'
import { getAllLanguagesList } from '../../../../services/languages'
import { editProduct } from '../../../../services/products'
import { notificationError, notificationSuccess } from '../../../ui/Notifications'

const defaultForm = {
  originalLanguageId: 0,
  originalLanguage: '',
  originalTitle: '',
  originalDescription: '',
  originalUrl: '',
  originalSourceUrl: '',
  originalCountryId: 0,
  originalCountry: '',
  originalProductId: 0,
}

const ProductOrigin = ({ productId, reloadProduct }) => {
  const { isLoadingProduct, origin, variants, minimumPayload } = useSelector(state => state.product_detail)
  const [formData, setFormData] = useState(defaultForm)
  const [formDataVariants, setFormDataVariants] = useState([])
  // language
  const [languageList, setLanguageList] = useState([])
  // country
  const [countryList, setCountryList] = useState([])
  // service
  const [loadingSubmit, setLoadingSubmit] = useState(false)

  const assignToForm = () => {
    const data = {
      originalLanguageId: `${origin.originalLanguageId}`,
      originalLanguage: origin.originalLanguage,
      originalTitle: origin.originalTitle,
      originalDescription: origin.originalDescription || '',
      originalUrl: origin.originalUrl,
      originalSourceUrl: origin.originalSourceUrl,
      originalCountryId: origin.originalCountryId,
      originalCountry: origin.originalCountry,
      originalProductId: origin.originalProductId
    }

    setFormData(data)
    setFormDataVariants(JSON.parse(JSON.stringify(variants.origin)))
  }

  const handleGetListLanguage = async () => {
    try {
      const response = await getAllLanguagesList()
      const dataLanguage = response.data
      const remapDataLanguage = dataLanguage.map((val) => {
        return {
          value: `${val.attributes.id}`,
          label: val.attributes.name
        }
      })
      setLanguageList(remapDataLanguage)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetListCountry = async () => {
    const parameter = {
      'page[num]': 1,
      'page[limit]': 300,
      'fields[country]': 'name',
    }
    try {
      const response = await getAllCountryList(parameter)
      const dataCountry = response.data
      const remapDataCountry = dataCountry.map((val) => {
        return {
          value: `${val.attributes.id}`,
          label: val.attributes.name
        }
      })
      setCountryList(remapDataCountry)
    } catch (error) {
      console.log(error)
    }
  }


  useEffect(() => {
    if (!isLoadingProduct && origin) {
      assignToForm()
      handleGetListCountry()
      handleGetListLanguage()
    }
    // eslint-disable-next-line
  }, [isLoadingProduct, origin])

  useEffect(() => {
    const findName = countryList?.find(e => e.value === `${formData.originalCountryId}`)?.label || ''
    handleChangeForm('originalCountry', findName)
    // eslint-disable-next-line
  }, [formData.originalCountryId])

  useEffect(() => {
    const findName = languageList?.find(e => e.value === `${formData.originalLanguageId}`)?.label || ''
    handleChangeForm('originalLanguage', findName)
    // eslint-disable-next-line
  }, [formData.originalLanguageId])

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }
  const handleChangeOriginalNameVariant = (indexVariant, val) => {
    const form = JSON.parse(JSON.stringify(formDataVariants))
    form[indexVariant].original_name = val
    setFormDataVariants(form)
  }
  const handleChangeOriginalNameVariantOption = (indexVariant, indexOption, val) => {
    const form = JSON.parse(JSON.stringify(formDataVariants))
    form[indexVariant].attributes[indexOption].original_value = val
    setFormDataVariants(form)
  }

  // description input config
  const configEditor = {
    plugins:[
      Bold,
      Essentials,
      Italic,
      Mention,
      Paragraph,
      Undo,
      Font,
      Heading,
      List,
      Alignment,
      Link,
      PasteFromOffice,
      ImageInsertViaUrl,
      Image,
      ImageInsert,
      Table, TableToolbar,
    ],
    alignment: {
      options: [ 'left', 'right' ]
    },
    fontSize: {
      options: [
          9,
          10,
          11,
          12,
          13,
          14,
          15,
          16,
          17,
          18,
          19,
          20,
          21,
          22
      ],
      supportAllValues: true
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true
      }
    },
    link: {
      decorators: {
        addTargetToExternalLinks: {
          mode: 'automatic',
          callback: url => /^(https?:)?\/\//.test( url ),
          attributes: {
              target: '_blank',
              rel: 'noopener noreferrer'
          }
        }
      }
    }, 
    toolbar: {
      items: [
        'undo', 'redo',
        '|',
        'heading',
        '|',
        'fontSize', 'Bold', 'Italic', 'alignment',
        '|',
        'link',
        '|',
        'bulletedList', 'numberedList',
        '|',
        'insertImage',
        '|',
        'insertTable'
      ],
      shouldNotGroupWhenFull: true
    },
    initialData: ''
  }

  // service
  const generatePayload = () => {
    const attributes = formDataVariants.flatMap((variant) => {
      return variant.attributes.map(e => {
        return {
          key_id: variant.key_id,
          key: variant.name,
          original_key: variant.original_name,
          value:  e.value, 
          value_id: e.id,
          property: '',
          image_url: e.image_url,
          original_value: e.original_value
        }
      })
    })
    return {
      ...minimumPayload,
      original_language_id: formData.originalLanguageId,
      original_language: formData.originalLanguage,
      original_title: formData.originalTitle,
      original_description: formData.originalDescription,
      original_url: formData.originalUrl,
      original_source_url: formData.originalSourceUrl,
      original_product_id: formData.originalProductId,
      original_country: formData.originalCountry,
      original_country_id: formData.originalCountryId,
      skus: variants.sku,
      attributes,
    }
  }

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true)
      const payload = generatePayload()
      const response = await editProduct(productId, payload)
      if (response.success) {
        notificationSuccess('Success', 'Update Product Success')
        reloadProduct()
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response?.data?.message || 'Please contact your administrator'
      notificationError('Update Product Error', errorMessage)
    } finally {
      setLoadingSubmit(false)
    }
  }

  const renderButtonUpdate = () => {
    const buttonStyle = {
      position: 'fixed',
      bottom: '25px',
      right: '55px',
      padding: '10px',
      zIndex: 1000,
    }
    return (
      <Button
        size='sm'
        style={buttonStyle}
        loading={loadingSubmit}
        loaderProps={{ type: 'dots' }}
        onClick={() => handleSubmit()}
      >
        Update
      </Button>
    )
  }

  const mappingVariatnts = (formDataVariants) => formDataVariants.map((variant, indexVariant) => {
    const mappingOptionName = (options) => options.map((options, indexOption) => {
      return (
        <TextInput
          key={indexOption}
          value={formDataVariants[indexVariant].attributes[indexOption].value}
          label="Variant Options"
          placeholder="Input Here"
          disabled
        />
      )
    })
    const mappingOriginalOptionName = (options) => options.map((options, indexOption) => {
      return (
        <TextInput
          key={indexOption}
          value={formDataVariants[indexVariant].attributes[indexOption].original_value}
          label="Origin Variant Options"
          placeholder="Input Here"
          onChange={(event) => handleChangeOriginalNameVariantOption(indexVariant, indexOption, event.currentTarget.value)}
        />
      )
    })

    return (
      <SimpleGrid key={indexVariant} cols={4} spacing="xl" verticalSpacing="xl">
        <TextInput
          value={formDataVariants[indexVariant].name}
          label="Variant Name"
          placeholder="Input Here"
          disabled
        />

        <TextInput
          value={formDataVariants[indexVariant].original_name}
          label="Origin Variant Name"
          placeholder="Input Here"
          onChange={(event) => handleChangeOriginalNameVariant(indexVariant, event.currentTarget.value)}
        />

        <Stack>
          {mappingOptionName(variant.attributes)}
        </Stack>

        <Stack>
          {mappingOriginalOptionName(variant.attributes)}
        </Stack>
      </SimpleGrid>
    )
  })

  return (
    <Box mb={20} mt={10}>
      <Paper my={'xl'} p={'md'} withBorder shadow="xs" radius="md">
        <Text fw={500}>
          Product Origin Language
        </Text>

        <Divider my={10} />
        <Stack style={{ width: '75%'}} gap={'lg'}>
          <Select
            name='language-origin'
            label='Product Origin Language'
            value={formData.originalLanguageId}
            data={languageList}
            onChange={(val) => handleChangeForm('originalLanguageId', val)}
            allowDeselect={false}
          />
          <TextInput
            value={formData.originalTitle}
            label="Product Origin Title"
            placeholder="Input Product Name Here"
            style={{ marginTop: '10px' }}
            onChange={(val) => handleChangeForm('originalTitle', val.target.value)}
          />

          <Box>
            <Text size="sm" fw={500}>
              Product Origin Description
            </Text>
            <CKEditor
              editor={ClassicEditor}
              config={configEditor}
              data={formData.originalDescription}
              onReady={(editor) => {
                editor.execute('fontSize', { value: '12px' })
                editor.editing.view.change((writer) => {
                  writer.setStyle(
                    'height',
                    '250px',
                    editor.editing.view.document.getRoot()
                  )
                })
              }}
              onChange={( event, editor ) => {
                editor.execute('fontSize', { value: '12px' })
                const valueEditor = editor.getData()
                handleChangeForm('originalDescription', valueEditor)
              }}
            />
          </Box>

          <TextInput
            value={formData.originalUrl}
            label="Product Origin Url"
            placeholder="Input Here"
            onChange={(val) => handleChangeForm('originalUrl', val.target.value)}
          />

          <TextInput
            value={formData.originalSourceUrl}
            label="Product Url Origin Source"
            placeholder="Input Here"
            onChange={(val) => handleChangeForm('originalSourceUrl', val.target.value)}
          />

          <Select
            name='country-origin'
            label='Country Origin'
            value={formData.originalCountryId}
            data={countryList}
            searchable
            onChange={(val) => handleChangeForm('originalCountryId', val)}
            allowDeselect={false}
          />
        </Stack>
      </Paper>

      <Paper my={'xl'} p={'md'} withBorder shadow="xs" radius="md">
        <Text fw={500}>
          Variant Origin Language
        </Text>

        <Divider my={10} />

        <Stack>
          {mappingVariatnts(formDataVariants)}
        </Stack>

      </Paper>

      {renderButtonUpdate()}
    </Box>
  )
}

export default ProductOrigin